/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo, useEffect } from 'react';
import PowderLotSelection from './powderLotSelection';
import IncubationParameters from './incubationParameters';
import MicrobialGrowthRecording from './microbialGrowthRecording';
import QualityAssuranceVerdict from './qualityAssuranceVerdict';
import MediaPrepHeaderSection from '../../../../common/commonComponents/mediaPrepheaderSection';
import MediaPrepPageHeader from '../../../../common/commonComponents/mediaPrepPageHeader';
import { useTranslation } from 'react-i18next';
import { gptTestService } from "../../../../../service/gptTestServices";
import { MediaPowderService } from '../../../../../service/MediaPowderServices';
import GptTestConfirmModal from '../partials/GptTestConfirmModal';
import CustomToast from '../../../../../widgets/toaster';

import {
  OrganismTest,
  GPTBatchDetails,
  GPTLot,
  OrganismType,
  Incubator,
  GPTValidationErrors,
  GPTPayload,
} from "../GPTTest.types";

interface GPTTestDetailsProps {
  batchId: string;
  onFinish: () => void;
  showToastMessage: (message: string, type: string) => void;
}


const GPTTestDetails: React.FC<GPTTestDetailsProps> = ({ batchId, onFinish, showToastMessage }) => {
  const { t } = useTranslation();
  const [batchDetails, setBatchDetails] = useState<GPTBatchDetails | null>(null);
  const [availableLots, setAvailableLots] = useState<GPTLot[]>([]);
  const [organismTypes, setOrganismTypes] = useState<OrganismType[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [gptStatus, setGptStatus] = useState("");
  const [finalRemarks, setFinalRemarks] = useState('');
  const [incubators, setIncubators] = useState<Incubator[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFileId, setUploadedFileId] = useState<number | null>(null);
  const [errors, setErrors] = useState<GPTValidationErrors>({});

  const [organisms, setOrganisms] = useState<OrganismTest[]>([]);

  const getBatchDetails = async () => {
    try {
      const response = await gptTestService.getMediaPowderListDetailsGpt(
        1,
        10,
        "id",
        "desc",
        "",
        batchId,
        ""
      );

      if (response.status && response.statusCode === 200) {
        const batchData = response.data[0];
        setBatchDetails({
          id: batchData.id,
          batchCode: batchData.batch_name,
          name: batchData.media_name,
          manufacturer: batchData.manufacturer_name,
          inventoryRef: batchData.inventoryRef,
        });
      } else {
        setBatchDetails(null);
      }
    } catch (error) {
      console.error("Batch details API error:", error);
    }
  };



  const getBatchLotDetails = async () => {
    try {
      const res = await MediaPowderService.getMediaPowderLotDetails(batchId);

      if (res?.status) {
        const mappedLots: GPTLot[] = (res.data || []).map((lot: any) => ({
          id: lot.id,
          lotCode: lot.lot_code,
          available: parseFloat(lot.available_stock || 0),
        }));

        setAvailableLots(mappedLots);
      }
    } catch (error) {
      console.error("Lot details API error:", error);
    }
  };


  const getOrganismTypes = async () => {
      try {
        const response = await gptTestService.listOrganismTypes();

        if (response?.status && response?.statusCode === 200) {
          setOrganismTypes(response.data || []);
        }
      } catch (error) {
        console.error("Organism types API error:", error);
      }
    };


  const getIncubators = async () => {
    try {
      const response = await gptTestService.getIncubatorList();
      if (response?.status && response?.statusCode === 200) {
        setIncubators(response.data || []);
      }
    } catch (error) {
      console.error("Incubator API error:", error);
    }
  };




    // Section 0: Powder Lot Selection
  const [powderData, setPowderData] = useState({
    selectedLot: 0,
    quantityUsed: '',
  });


  const organismPayload = organisms
  .map((item) => {
    const selectedOrganism = organismTypes.find(
      (org) => org.organism_type === item.name
    );

    if (!selectedOrganism) return null;

    return {
      organism_type_id: selectedOrganism.id,
      growth_observed: item.observed ? 1 : 0,
      cfu_count: item.cfu ? Number(item.cfu) : 0,
    };
  })
  .filter(
    (
      item
    ): item is {
      organism_type_id: number;
      growth_observed: number;
      cfu_count: number;
    } => item !== null
  );


  // const handleAction = (type: 'APPROVED' | 'REJECTED') => {
  //   setGptStatus(type);
  //   setShowConfirmModal(true);
  // };
  const handleAction = (type: "APPROVED" | "REJECTED") => {
    const newErrors: GPTValidationErrors = {};
    
    if (!incubationData.testDate) {newErrors.testDate = t("gptTest.gptTestDetails.errTestDate");}
    if (!powderData.selectedLot) {newErrors.selectedLot = t("gptTest.gptTestDetails.errLot");}
    if (!powderData.quantityUsed) {newErrors.quantityUsed = t("gptTest.gptTestDetails.errQnty");}
    if (!incubationData.startDate) {newErrors.startDate = t("gptTest.gptTestDetails.errStartDate");}
    if (incubationData.startDate && incubationData.endDate) {
      const start = new Date(incubationData.startDate).getTime();
      const end = new Date(incubationData.endDate).getTime();
      const diffHours = (end - start) / (1000 * 60 * 60);
      if (diffHours < 48) {
        newErrors.endDate = t("gptTest.gptTestDetails.minHrs");
      }
    }
    if (!incubationData.endDate) {newErrors.endDate = t("gptTest.gptTestDetails.errEndDate");}
    if (!incubationData.temperature) {newErrors.temperature = t("gptTest.gptTestDetails.errTemp");}
    if (!incubationData.incubatorId) {newErrors.incubatorId = t("gptTest.gptTestDetails.errIncubator");}
    if (organisms.length > 0) {
      organisms.forEach((org, index) => {
        if (!org.name) {
          newErrors[`organism_${index}`] = t("gptTest.gptTestDetails.errMicro");
        }
      });
    }
    if (!selectedFile) {newErrors.file = t("gptTest.gptTestDetails.errFile");}
    if (!finalRemarks.trim()) {newErrors.qaRemarks = t("gptTest.gptTestDetails.errQaRemarks");}
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setGptStatus(type);
    setShowConfirmModal(true);
  };



  const selectedLotInfo = useMemo(() => {
    return availableLots.find(l => l.id === powderData.selectedLot);
  }, [powderData.selectedLot]);



  // Section 1: Incubation
  const [incubationData, setIncubationData] = useState({
    testDate: new Date().toISOString().split('T')[0],
    startDate: '',
    endDate: '',
    temperature: '',
    incubatorId: ''
  });

  // Auto-calculation for duration with enhanced readability
  const durationInfo = useMemo(() => {
    if (incubationData.startDate && incubationData.endDate) {
      const start = new Date(incubationData.startDate).getTime();
      const end = new Date(incubationData.endDate).getTime();
      const diff = end - start;
      if (diff > 0) {
        const hours = (diff / (1000 * 60 * 60));
        return {
          value: hours.toFixed(1),
          unit: 'Hours',
          isValid: hours >= 24 // Example SOP: minimum 24h
        };
      }
    }
    return { value: '--', unit: 'Hours', isValid: false };
  }, [incubationData.startDate, incubationData.endDate]);


  const formatDateTime = (date: string) => {
    if (!date) return '';
    return `${date.replace('T', ' ')}:00`;
  };

  const payload: GPTPayload = useMemo(() => ({
    batch_id: Number(batchId),
    lot_id: Number(powderData.selectedLot),
    gpt_test_date: incubationData.testDate,
    powder_quantity_consumed: Number(powderData.quantityUsed),
    incubation_start_date: formatDateTime(incubationData.startDate),
    incubation_end_date: formatDateTime(incubationData.endDate),
    temperature: Number(incubationData.temperature),
    incubator_id: Number(incubationData.incubatorId),
    organism_data: organismPayload,
    lab_remarks: finalRemarks,
    certification_remarks: finalRemarks,
    file_id: uploadedFileId,
    status: gptStatus,
  }), [
    batchId,
    powderData,
    incubationData,
    organismPayload,
    finalRemarks,
    uploadedFileId,
    gptStatus,
  ]);


  useEffect(() => {
    getBatchDetails();
    getBatchLotDetails();
    getOrganismTypes();
    getIncubators();
  }, [batchId]);  



  const addOrganism = () => {
    setOrganisms([...organisms, { 
      id: Math.random().toString(36).substr(2, 9), 
      name: '', 
      observed: false, 
      remarks: '' 
    }]);
  };


  const removeOrganism = (id: string) => {
    setOrganisms(organisms.filter(o => o.id !== id));
  };


  const updateOrganism = (id: string, field: keyof OrganismTest, value: any) => {
    setOrganisms(organisms.map(o => o.id === id ? { ...o, [field]: value } : o));
  };

  
  // Validation Logic
  const isPowderSelected = useMemo(() => {
    return !!(powderData.selectedLot && powderData.quantityUsed);
  }, [powderData]);
  const isIncubationComplete = useMemo(() => {
    return !!(incubationData.startDate && incubationData.endDate && incubationData.temperature && incubationData.incubatorId);
  }, [incubationData]);
  const hasOrganisms = useMemo(() => organisms.length > 0, [organisms]);
  const canSubmit = useMemo(() => {
    const organismsValid = organisms.length > 0 && organisms.every(o => o.name.trim() !== '');
    return isPowderSelected && isIncubationComplete && hasOrganisms && organismsValid && finalRemarks.trim() !== '';
  }, [isPowderSelected, isIncubationComplete, hasOrganisms, organisms, finalRemarks]);


  return (
    <div className="media-prep-scope">
      {/* Sticky Header */}
      <div className="sticky top-[110px] z-40 bg-white border-b border-slate-200 px-8 py-2 flex items-start justify-between shadow-sm">
        <div className="flex items-center w-[330px]">
          <button
            onClick={onFinish}
            className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-all mr-6"
          >
            <span className="material-symbols-outlined text-xl">
              {t('mediaPowder.icons.arrow_back')}
            </span>
          </button>
        <div>
            <h2 className="text-xl font-black text-[#2D3E50] tracking-tight uppercase">{t('gptTest.gptTestDetails.title')}</h2>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{t('gptTest.gptTestDetails.description')}</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="text-right">
             <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('gptTest.gptTestDetails.activeAnalysis')}</p>
             <p className="text-sm font-black text-[#2D3E50] font-mono tracking-tight">{batchDetails?.batchCode || '--'}</p>
          </div>
          <div className="h-10 w-px bg-slate-100"></div>
          <div className="text-right">
             <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('gptTest.gptTestDetails.processStatus')}</p>
             <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 rounded-full border border-amber-100">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                <span className="relative top-[0.5px] text-[9px] font-black text-amber-600 uppercase tracking-wider leading-none">
                    {t("gptTest.gptTestDetails.status")}
                </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area - Long Vertical Scroll */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-10 space-y-12 max-w-7xl mx-auto w-full pb-40">

          {/* Section 0: Powder Lot Selection */}
          <PowderLotSelection
            batchId={batchId}
            batchDetails={batchDetails}
            incubationData={incubationData}
            setIncubationData={setIncubationData}
            powderData={powderData}
            setPowderData={setPowderData}
            availableLots={availableLots}
            selectedLotInfo={selectedLotInfo}
            errors={errors}
            setErrors={setErrors}
          />
          
          {/* Section 1: Incubation Parameters */}
          <IncubationParameters
            incubationData={incubationData}
            setIncubationData={setIncubationData}
            durationInfo={durationInfo}
            incubators={incubators}
            errors={errors}
            setErrors={setErrors}
          />


          {/* Section 2: Lab Growth Recording */}
          <MicrobialGrowthRecording
            organisms={organisms}
            addOrganism={addOrganism}
            removeOrganism={removeOrganism}
            updateOrganism={updateOrganism}
            organismTypes={organismTypes}
            errors={errors}
            setErrors={setErrors}
          />

          {/* Section 3: QA Decision Section */}
          <QualityAssuranceVerdict
            qaRemarks={finalRemarks}
            setQaRemarks={setFinalRemarks}
            canSubmit={canSubmit}
            handleAction={handleAction}
            errors={errors}
            setErrors={setErrors}

            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            uploadedFileId={uploadedFileId}
            setUploadedFileId={setUploadedFileId}
          />
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <GptTestConfirmModal
          payload={payload}
          batchName={batchDetails?.batchCode || ""}
          toggleModal={() => setShowConfirmModal(false)}
          handlePostSuccess={onFinish}
          showToastMessage={showToastMessage}
        />
      )}
    </div>
  );
};

export default GPTTestDetails;
